
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  loadLeagueData,
  saveLeagueData,
  weekSchedule,
  type LeagueData,
} from '../../utils/localStorage';

const HANDICAP_STORAGE_KEY = 'mbc_handicap_data';

interface HandicapData {
  [playerId: string]: {
    [period: string]: number; // period like "w1-4", "w5-8", "w9-12"
  };
}

function loadHandicapData(): HandicapData {
  try {
    const stored = localStorage.getItem(HANDICAP_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.error('Error loading handicap data:', e);
  }
  return {};
}

function saveHandicapData(data: HandicapData) {
  localStorage.setItem(HANDICAP_STORAGE_KEY, JSON.stringify(data));
}

const handicapPeriods = [
  { key: 'w1-4', label: 'Wk 1-4' },
  { key: 'w5-8', label: 'Wk 5-8' },
  { key: 'w9-12', label: 'Wk 9-12' },
];

const weekColumns = [
  { weeks: [1, 2], label: 'Wk 1&2' },
  { weeks: [3, 4], label: 'Wk 3&4' },
  { weeks: [5, 6], label: 'Wk 5&6' },
  { weeks: [7, 8], label: 'Wk 7&8' },
  { weeks: [9, 10], label: 'Wk 9&10' },
  { weeks: [11, 12], label: 'Wk 11&12' },
];

// Player display order matching the spreadsheet
const playerOrder = [
  'todd', 'steve', 'riley', 'benny', 'jake', 'braxton',
  'joshm', 'tanner', 'tom', 'joshe', 'payton', 'zoe'
];

export default function ScorecardPage() {
  const [leagueData, setLeagueData] = useState<LeagueData>(loadLeagueData());
  const [handicapData, setHandicapData] = useState<HandicapData>(loadHandicapData());
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [passcode, setPasscode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [showPasscode, setShowPasscode] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const players = Object.values(leagueData.players);
  const orderedPlayers = playerOrder.map(id => players.find(p => p.id === id)).filter(Boolean) as typeof players;

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  const handleCellClick = (cellKey: string, currentValue: string) => {
    if (!isVerified) {
      setPendingEdit(cellKey);
      setPasscode('');
      setShowPasscode(true);
      setEditValue(currentValue);
      return;
    }
    setEditingCell(cellKey);
    setEditValue(currentValue);
  };

  const verifyAndEdit = () => {
    if (passcode === 'mbc909') {
      setIsVerified(true);
      setShowPasscode(false);
      if (pendingEdit) {
        setEditingCell(pendingEdit);
        setPendingEdit(null);
      }
    } else {
      alert('Incorrect passcode');
    }
  };

  const saveCell = (cellKey: string, value: string) => {
    const parts = cellKey.split('|');
    const type = parts[0]; // 'gross', 'net', 'handicap'
    const playerId = parts[1];

    if (type === 'gross' || type === 'net') {
      const week = parseInt(parts[2]);
      const numVal = value === '' ? null : parseInt(value);
      const newData = { ...leagueData };

      if (numVal === null) {
        // Clear the score for this type
        if (newData.scores[playerId]?.[week]) {
          if (type === 'gross') {
            newData.scores[playerId][week].gross = 0;
          } else {
            newData.scores[playerId][week].net = 0;
          }
        }
      } else {
        if (!newData.scores[playerId]) newData.scores[playerId] = {};
        if (!newData.scores[playerId][week]) {
          newData.scores[playerId][week] = { gross: 0, net: 0 };
        }
        if (type === 'gross') {
          newData.scores[playerId][week].gross = numVal;
        } else {
          newData.scores[playerId][week].net = numVal;
        }
      }

      saveLeagueData(newData);
      setLeagueData(newData);
    } else if (type === 'handicap') {
      const period = parts[2];
      const numVal = value === '' ? 0 : parseInt(value);
      const newHcp = { ...handicapData };
      if (!newHcp[playerId]) newHcp[playerId] = {};
      newHcp[playerId][period] = numVal;
      saveHandicapData(newHcp);
      setHandicapData(newHcp);
    }

    setEditingCell(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, cellKey: string) => {
    if (e.key === 'Enter') {
      saveCell(cellKey, editValue);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const getScore = (playerId: string, week: number, type: 'gross' | 'net'): string => {
    const score = leagueData.scores[playerId]?.[week];
    if (!score) return '';
    const val = type === 'gross' ? score.gross : score.net;
    return val ? val.toString() : '';
  };

  const getHandicap = (playerId: string, period: string): string => {
    const val = handicapData[playerId]?.[period];
    return val ? val.toString() : '';
  };

  // Find which week a player actually plays in a bi-weekly pair
  const getPlayerWeekInPair = (playerId: string, weekPair: number[]): number | null => {
    for (const w of weekPair) {
      const weekData = weekSchedule.find(ws => ws.week === w);
      if (weekData) {
        const isPlaying = weekData.pairings.some(p => p.player1 === playerId || p.player2 === playerId);
        if (isPlaying) return w;
      }
    }
    return null;
  };

  const renderCell = (cellKey: string, value: string, isGross: boolean) => {
    if (editingCell === cellKey) {
      return (
        <input
          ref={inputRef}
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => saveCell(cellKey, editValue)}
          onKeyDown={(e) => handleKeyDown(e, cellKey)}
          className="w-full h-full bg-emerald-500/20 border border-emerald-500 text-white text-center text-[11px] font-bold outline-none px-0.5"
          style={{ minWidth: 0 }}
        />
      );
    }

    return (
      <button
        onClick={() => handleCellClick(cellKey, value)}
        className={`w-full h-full text-center text-[11px] font-semibold transition-colors hover:bg-slate-600/50 ${
          isGross ? 'text-slate-300' : 'text-emerald-400'
        } ${value ? '' : 'text-slate-600'}`}
      >
        {value || '-'}
      </button>
    );
  };

  const renderHandicapCell = (cellKey: string, value: string) => {
    if (editingCell === cellKey) {
      return (
        <input
          ref={inputRef}
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => saveCell(cellKey, editValue)}
          onKeyDown={(e) => handleKeyDown(e, cellKey)}
          className="w-full h-full bg-yellow-500/20 border border-yellow-500 text-white text-center text-[11px] font-bold outline-none px-0.5"
          style={{ minWidth: 0 }}
        />
      );
    }

    return (
      <button
        onClick={() => handleCellClick(cellKey, value)}
        className={`w-full h-full text-center text-[11px] font-bold transition-colors hover:bg-slate-600/50 text-yellow-400 ${value ? '' : 'text-slate-600'}`}
      >
        {value || '-'}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="bg-slate-800 px-4 py-4 border-b border-slate-700 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <Link to="/" className="w-9 h-9 flex items-center justify-center bg-slate-700 rounded-full text-white hover:bg-slate-600 transition-colors">
            <i className="ri-arrow-left-line text-base"></i>
          </Link>
          <div className="text-center flex-1">
            <h1 className="text-lg font-black text-white">SCORECARD</h1>
            <p className="text-emerald-400 text-[10px] font-bold">WEEKLY SCORES & HANDICAPS</p>
          </div>
          {isVerified ? (
            <div className="bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full text-[10px] font-bold">
              UNLOCKED
            </div>
          ) : (
            <button
              onClick={() => { setPasscode(''); setShowPasscode(true); setPendingEdit(null); }}
              className="bg-blue-500 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold hover:bg-blue-600 transition-colors"
            >
              UNLOCK
            </button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="px-4 py-3 bg-slate-800/50 flex items-center gap-4 text-[10px]">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-slate-300"></div>
          <span className="text-slate-400">Gross</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
          <span className="text-slate-400">Net</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
          <span className="text-slate-400">Handicap</span>
        </div>
        <div className="text-slate-500 ml-auto">Tap cell to edit</div>
      </div>

      {/* Scrollable Table */}
      <div ref={tableRef} className="overflow-x-auto overflow-y-auto">
        <table className="w-full border-collapse" style={{ minWidth: '700px' }}>
          {/* Table Header */}
          <thead className="sticky top-0 z-[5]">
            <tr className="bg-slate-800">
              <th className="sticky left-0 z-[6] bg-slate-800 text-left px-2 py-2 text-[10px] text-slate-400 font-bold border-b border-slate-700 min-w-[100px]">
                PLAYER
              </th>
              <th className="bg-slate-800 px-1 py-2 text-[10px] text-slate-400 font-bold border-b border-slate-700 min-w-[28px]">
                G/N
              </th>
              {weekColumns.map((wc) => (
                <th key={wc.label} className="bg-slate-800 px-1 py-2 text-[10px] text-emerald-400 font-bold border-b border-slate-700 text-center min-w-[44px]">
                  {wc.label}
                </th>
              ))}
              {handicapPeriods.map((hp) => (
                <th key={hp.key} className="bg-slate-800 px-1 py-2 text-[10px] text-yellow-400 font-bold border-b border-slate-700 text-center min-w-[44px]">
                  HCP<br />{hp.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {orderedPlayers.map((player) => (
              <>
                {/* Gross Row */}
                <tr key={`${player.id}-gross`} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td rowSpan={2} className="sticky left-0 z-[3] bg-slate-900 px-2 py-1 border-b border-slate-700/50">
                    <div className="text-white font-bold text-[11px] leading-tight">{player.name}</div>
                    <div className="text-slate-500 text-[9px] leading-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[90px]">{player.teamName}</div>
                  </td>
                  <td className="text-center text-[9px] text-slate-500 font-semibold bg-slate-900/50 px-1 py-1">G</td>
                  {weekColumns.map((wc) => {
                    const playWeek = getPlayerWeekInPair(player.id, wc.weeks);
                    const cellKey = `gross|${player.id}|${playWeek || wc.weeks[0]}`;
                    const value = playWeek ? getScore(player.id, playWeek, 'gross') : '';
                    return (
                      <td key={wc.label} className="px-0.5 py-0.5 h-7 border-b border-slate-800/30">
                        {playWeek ? renderCell(cellKey, value, true) : (
                          <div className="w-full h-full bg-slate-800/20 text-center text-slate-700 text-[10px]">—</div>
                        )}
                      </td>
                    );
                  })}
                  {/* Handicap cells only on first row, rowspan 2 */}
                  {handicapPeriods.map((hp) => {
                    const cellKey = `handicap|${player.id}|${hp.key}`;
                    const value = getHandicap(player.id, hp.key);
                    return (
                      <td key={hp.key} rowSpan={2} className="px-0.5 py-0.5 h-7 border-b border-slate-700/50 bg-slate-800/20">
                        {renderHandicapCell(cellKey, value)}
                      </td>
                    );
                  })}
                </tr>
                {/* Net Row */}
                <tr key={`${player.id}-net`} className="border-b border-slate-700/50 hover:bg-slate-800/30">
                  <td className="text-center text-[9px] text-emerald-500 font-semibold bg-slate-900/50 px-1 py-1">N</td>
                  {weekColumns.map((wc) => {
                    const playWeek = getPlayerWeekInPair(player.id, wc.weeks);
                    const cellKey = `net|${player.id}|${playWeek || wc.weeks[0]}`;
                    const value = playWeek ? getScore(player.id, playWeek, 'net') : '';
                    return (
                      <td key={wc.label} className="px-0.5 py-0.5 h-7 border-b border-slate-700/30">
                        {playWeek ? renderCell(cellKey, value, false) : (
                          <div className="w-full h-full bg-slate-800/20 text-center text-slate-700 text-[10px]">—</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Course Legend */}
      <div className="px-4 py-4 bg-slate-800/50 mt-4">
        <h3 className="text-white font-bold text-xs mb-2">COURSE SCHEDULE</h3>
        <div className="grid grid-cols-2 gap-2">
          {weekColumns.map((wc, i) => {
            const weekData = weekSchedule.find(w => w.week === wc.weeks[0]);
            return (
              <div key={i} className="bg-slate-800 rounded-lg p-2">
                <div className="text-emerald-400 text-[10px] font-bold">{wc.label}</div>
                <div className="text-slate-300 text-[10px]">{weekData?.course || 'TBD'}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-8 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-emerald-600 transition-colors"
        >
          <i className="ri-home-line"></i>
          BACK TO HOME
        </Link>
      </div>

      {/* Passcode Modal */}
      {showPasscode && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700">
            <h3 className="text-white font-black text-lg mb-4">Enter Passcode</h3>
            <input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white mb-4"
              placeholder="Enter passcode"
              onKeyDown={(e) => e.key === 'Enter' && verifyAndEdit()}
            />
            <div className="flex gap-3">
              <button onClick={verifyAndEdit} className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors">VERIFY</button>
              <button onClick={() => { setShowPasscode(false); setPendingEdit(null); }} className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors">CANCEL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
